import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Grid,
  Button,
  Autocomplete,
  IconButton,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import api from "../services/api";
import StickerPrintArea, { STICKER_PRINT_CSS } from "./StickerPrintArea";
import { todayISO } from "../utils/date";

const PACK_TYPES = ["Boxes", "Pallets", "Bales"];
const STATUS_OPTIONS = ["All", "Active", "InPackingList", "Shipped"];

const today = () => todayISO();

const emptyForm = {
  Box_ID: null,
  StickerCode: "",
  Item_ID: null,
  Item_Description: "",
  PackType: "Boxes",
  PackedAt: today(),
  GrossWt: "",
  TareWt: "",
  Notes: "",
};

export default function FinishedGoods() {
  const [products, setProducts] = useState([]);

  // ---- Print plumbing ----
  // We print with a scoped print stylesheet + window.print() rather than
  // react-to-print: react-to-print prints via a hidden iframe that mobile
  // browsers (iOS Safari / Android Chrome) ignore, so they print the whole
  // screen. STICKER_PRINT_CSS (injected only while printing) hides everything
  // except the off-screen sticker area and applies the 4x6 @page, which works
  // identically on desktop and mobile.
  const [printQueue, setPrintQueue] = useState([]);

  // Tear down print mode once the OS print dialog closes or is cancelled.
  useEffect(() => {
    const cleanup = () => {
      document.body.classList.remove("printing-sticker");
      const s = document.getElementById("sticker-print-style");
      if (s) s.remove();
      setPrintQueue([]);
    };
    window.addEventListener("afterprint", cleanup);
    return () => window.removeEventListener("afterprint", cleanup);
  }, []);

  // When something is queued, let the sticker (incl. QR) paint, then print.
  useEffect(() => {
    if (!printQueue.length) return;
    const id = setTimeout(() => {
      if (!document.getElementById("sticker-print-style")) {
        const s = document.createElement("style");
        s.id = "sticker-print-style";
        s.textContent = STICKER_PRINT_CSS;
        document.head.appendChild(s);
      }
      document.body.classList.add("printing-sticker");
      window.print();
    }, 60);
    return () => clearTimeout(id);
  }, [printQueue]);

  // ---- Form state ----
  const [form, setForm] = useState(emptyForm);
  const net = useMemo(() => {
    const g = Number(form.GrossWt) || 0;
    const t = Number(form.TareWt) || 0;
    return +(g - t).toFixed(2);
  }, [form.GrossWt, form.TareWt]);

  // ---- List state ----
  const [statusFilter, setStatusFilter] = useState("Active");
  const [itemFilter, setItemFilter] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);

  /* === Load dropdowns === */
  useEffect(() => {
    api.get("/packingList/products")
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load products:", e));
  }, []);

  /* === Fetch list === */
  const fetchRows = useCallback(() => {
    const params = {};
    if (statusFilter && statusFilter !== "All") params.status = statusFilter;
    if (itemFilter)  params.itemId   = itemFilter.id;
    if (fromDate)    params.fromDate = fromDate;
    if (toDate)      params.toDate   = toDate;
    if (search)      params.search   = search;
    api.get("/finishedGoods", { params })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load finished goods:", e));
  }, [statusFilter, itemFilter, fromDate, toDate, search]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  /* === Save (optionally print) === */
  const save = async (alsoPrint) => {
    if (!form.Item_ID) { alert("Pick a product"); return; }
    const g = Number(form.GrossWt) || 0;
    const t = Number(form.TareWt) || 0;
    if (g <= 0) { alert("Gross weight must be greater than 0"); return; }
    if (t < 0 || g < t) { alert("Tare must be between 0 and gross"); return; }

    const payload = {
      Item_ID: form.Item_ID,
      Item_Description: form.Item_Description,
      PackType: form.PackType,
      PackedAt: form.PackedAt,
      GrossWt: g,
      TareWt: t,
      Notes: form.Notes || "",
    };

    try {
      const res = form.Box_ID
        ? await api.put(`/finishedGoods/${form.Box_ID}`, payload)
        : await api.post(`/finishedGoods`, payload);
      const saved = res.data;
      const newBoxId = saved.Box_ID ?? form.Box_ID;

      const fullBox = {
        Box_ID: newBoxId,
        // CREATE returns StickerCode; UPDATE doesn't, so fall back to the one already in form state.
        StickerCode: saved.StickerCode || form.StickerCode,
        Item_Description: form.Item_Description,
        PackType: form.PackType,
        PackedAt: form.PackedAt,
        GrossWt: g,
        TareWt: t,
        NetWt: saved.NetWt ?? net,
      };

      if (alsoPrint) setPrintQueue([fullBox]);

      setForm({ ...emptyForm, PackType: form.PackType, PackedAt: form.PackedAt });
      fetchRows();
    } catch (err) {
      console.error("Save finished goods failed:", err);
      alert("Failed to save: " + (err.response?.data?.error || err.message));
    }
  };

  /* === Re-print sticker for an existing row === */
  const reprint = (row) => setPrintQueue([row]);

  /* === Edit row (only if Active) === */
  const editRow = (row) => {
    setForm({
      Box_ID: row.Box_ID,
      StickerCode: row.StickerCode || "",
      Item_ID: row.Item_ID,
      Item_Description: row.Item_Description || "",
      PackType: row.PackType || "Boxes",
      PackedAt: row.PackedAt ? String(row.PackedAt).split("T")[0] : today(),
      GrossWt: row.GrossWt ?? "",
      TareWt: row.TareWt ?? "",
      Notes: row.Notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => setForm(emptyForm);

  const del = async (row) => {
    if (!window.confirm(`Delete sticker ${row.StickerCode}?`)) return;
    try {
      await api.delete(`/finishedGoods/${row.Box_ID}`);
      fetchRows();
    } catch (err) {
      alert("Failed to delete: " + (err.response?.data?.error || err.message));
    }
  };

  const statusChip = (s) => {
    if (s === "Active")        return <Chip size="small" label="Active" color="success" />;
    if (s === "InPackingList") return <Chip size="small" label="On Packing List" color="warning" />;
    if (s === "Shipped")       return <Chip size="small" label="Shipped" />;
    return <Chip size="small" label={s} />;
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* ============ WEIGH-IN FORM ============ */}
      <Box p={1}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6">
            {form.Box_ID ? `Edit Box ${form.Box_ID}` : "New Finished-Goods Box"}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Weigh each box/pallet, assign a sticker, and add it to active inventory.
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Autocomplete
                options={products}
                getOptionLabel={(o) => o?.Product_Description || ""}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                value={products.find((p) => p.id === form.Item_ID) || null}
                onChange={(e, v) =>
                  setForm((f) => ({
                    ...f,
                    Item_ID: v?.id || null,
                    Item_Description: v?.Product_Description || "",
                  }))
                }
                renderInput={(params) => <TextField {...params} label="Product" fullWidth />}
              />
            </Grid>

            <Grid size={{ xs: 6, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="pt-label">Pack Type</InputLabel>
                <Select
                  labelId="pt-label"
                  label="Pack Type"
                  value={form.PackType}
                  onChange={(e) => setForm((f) => ({ ...f, PackType: e.target.value }))}
                >
                  {PACK_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                type="date"
                label="Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.PackedAt}
                onChange={(e) => setForm((f) => ({ ...f, PackedAt: e.target.value }))}
              />
            </Grid>

            <Grid size={{ xs: 6, md: 1.5 }}>
              <TextField
                label="Gross (lb)"
                type="number"
                fullWidth
                inputProps={{ step: "0.01", min: "0" }}
                value={form.GrossWt}
                onChange={(e) => setForm((f) => ({ ...f, GrossWt: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 1.5 }}>
              <TextField
                label="Tare (lb)"
                type="number"
                fullWidth
                inputProps={{ step: "0.01", min: "0" }}
                value={form.TareWt}
                onChange={(e) => setForm((f) => ({ ...f, TareWt: e.target.value }))}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Net (lb)"
                fullWidth
                value={net.toFixed(2)}
                InputProps={{ readOnly: true }}
                sx={{ "& input": { fontWeight: 700 } }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 9 }}>
              <TextField
                label="Notes (optional)"
                fullWidth
                value={form.Notes}
                onChange={(e) => setForm((f) => ({ ...f, Notes: e.target.value }))}
              />
            </Grid>
          </Grid>

          <Box mt={2} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={() => save(true)}>
              {form.Box_ID ? "Update & Print" : "Save & Print"}
            </Button>
            <Button variant="outlined" onClick={() => save(false)}>
              {form.Box_ID ? "Update" : "Save (no print)"}
            </Button>
            {form.Box_ID && <Button onClick={cancelEdit}>Cancel Edit</Button>}
          </Box>
        </Paper>
      </Box>

      {/* ============ LIST ============ */}
      <Box p={1}>
        <Typography variant="h6" sx={{ mb: 1 }}>Finished Goods Inventory</Typography>

        <Paper sx={{ p: 2, mb: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                options={products}
                getOptionLabel={(o) => o?.Product_Description || ""}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                value={itemFilter}
                onChange={(e, v) => setItemFilter(v || null)}
                renderInput={(params) => <TextField {...params} size="small" label="Product" />}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField type="date" label="From" size="small" InputLabelProps={{ shrink: true }} fullWidth
                value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField type="date" label="To" size="small" InputLabelProps={{ shrink: true }} fullWidth
                value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField label="Sticker / search" size="small" fullWidth
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 1 }}>
              <Button variant="contained" fullWidth onClick={fetchRows}>Search</Button>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sticker</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Gross</TableCell>
                <TableCell align="right">Tare</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ color: "text.secondary" }}>
                    No boxes match this filter
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.Box_ID}>
                  <TableCell sx={{ fontWeight: 600 }}>{r.StickerCode}</TableCell>
                  <TableCell>{r.PackedAt ? String(r.PackedAt).split("T")[0] : "-"}</TableCell>
                  <TableCell>{r.Item_Description || "-"}</TableCell>
                  <TableCell>{r.PackType}</TableCell>
                  <TableCell align="right">{(Number(r.GrossWt) || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">{(Number(r.TareWt)  || 0).toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {(Number(r.NetWt) || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>{statusChip(r.Status)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <IconButton size="small" onClick={() => reprint(r)} title="Re-print sticker">
                      <PrintIcon fontSize="small" />
                    </IconButton>
                    {r.Status === "Active" && (
                      <>
                        <IconButton size="small" onClick={() => editRow(r)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => del(r)} title="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Hidden print surface */}
      <StickerPrintArea boxes={printQueue} />
    </Box>
  );
}
